import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Link, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { actions as modalActions } from 'redux/modal';
import { routesAliases } from 'router/routesAliases';
import { ROLES } from 'appConstants';

const useStyles = makeStyles(theme => ({
  list: {
    margin: theme.spacing(1.5, 0, 0),
    paddingLeft: theme.spacing(2),
    maxHeight: 280,
    overflowY: 'auto',
  },
  item: {
    marginBottom: theme.spacing(1),
  },
}));

const formatSessionLabel = (session, t) => {
  const statusLabel = t(`trainingSessions.statuses.${session.status}`, {
    defaultValue: session.status,
  });
  const dateRange = [session.startDate, session.endDate].filter(Boolean).join(' → ') || '—';
  const station = session.stationName ? ` · ${session.stationName}` : '';
  return `#${session.id} · ${statusLabel} · ${dateRange}${station}`;
};

const SessionsImpactConfirmContent = ({ sessions, message, linkSessions }) => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const openSession = sessionId => {
    window.open(`${routesAliases.editTrainingSession}/${sessionId}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box>
      <Typography variant="body2">{message}</Typography>
      <ul className={classes.list}>
        {sessions.map(session => (
          <li key={session.id} className={classes.item}>
            {linkSessions ? (
              <Link
                component="button"
                type="button"
                color="primary"
                onClick={() => openSession(session.id)}
                style={{ textAlign: 'left' }}
              >
                {formatSessionLabel(session, t)}
              </Link>
            ) : (
              <Typography variant="body2" component="span">
                {formatSessionLabel(session, t)}
              </Typography>
            )}
            {session.description ? (
              <Typography variant="caption" display="block" color="textSecondary">
                {session.description}
              </Typography>
            ) : null}
          </li>
        ))}
      </ul>
    </Box>
  );
};

SessionsImpactConfirmContent.propTypes = {
  sessions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      description: PropTypes.string,
      startDate: PropTypes.string,
      endDate: PropTypes.string,
      status: PropTypes.string,
      stationName: PropTypes.string,
    }),
  ).isRequired,
  message: PropTypes.string.isRequired,
  linkSessions: PropTypes.bool,
};

SessionsImpactConfirmContent.defaultProps = {
  linkSessions: false,
};

export const openSessionsImpactConfirmModal = ({
  dispatch,
  t,
  sessions,
  onConfirm,
  onCancel,
  linkSessions = false,
}) => {
  const handleCancel = () => {
    dispatch(modalActions.closeModal());
    if (onCancel) {
      onCancel();
    }
  };

  dispatch(
    modalActions.openModal(
      {
        size: 'medium',
        title: t('trainingParts.sessionsImpactTitle'),
        modalType: 'notification',
        onClose: handleCancel,
        actions: [
          {
            key: 'confirm',
            Component: () => (
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={async () => {
                  dispatch(modalActions.closeModal());
                  await onConfirm();
                }}
              >
                {t('trainingParts.sessionsImpactSaveAnyway')}
              </Button>
            ),
          },
          {
            key: 'reject',
            Component: () => (
              <Button type="button" variant="outlined" onClick={handleCancel}>
                {t('buttons.cancel')}
              </Button>
            ),
          },
        ],
      },
      <SessionsImpactConfirmContent
        sessions={sessions}
        message={t('trainingParts.sessionsImpactMessage')}
        linkSessions={linkSessions}
      />,
    ),
  );
};

export const useSessionsImpactConfirm = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const role = useSelector(state => state.auth.profile?.role);
  const linkSessions = role !== ROLES.HYPER;

  return ({ sessions, onConfirm, onCancel }) =>
    openSessionsImpactConfirmModal({
      dispatch,
      t,
      sessions,
      onConfirm,
      onCancel,
      linkSessions,
    });
};

export default SessionsImpactConfirmContent;
